import React, { FC, CSSProperties, useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell } from 'recharts';
import { useAuth } from "../contexts/AuthContext";
import { apiUrl } from "../utils/api";
import DateTimePickerModal from '../components/DateTimePickerModal';
import { dropdownStyle } from '../components/StyledSelect';

// --- Type Definitions ---

interface TableViolation {
  id: string;
  date: string;
  station: string | number;
  brigade: string | number;
  type: string;
}

interface SummaryData {
  title: string;
  value: number;
  color: string;
}

interface ChartData {
  name: string;
  value: number;
  color: string;
}

// API types
interface RigItem { id: string; name: string; status?: string; well_id?: string; contractor_id?: string; id_int?: string }
interface WorkShift { id: string; number: number }
interface ViolationType { id: string; name: string }
interface ViolationsStats { week: number; month: number; year: number; months: [string, number][] }
interface ViolationListItem {
  id: string;
  timestamp?: string;
  date?: string;
  station?: string;
  station_name?: string;
  station_number?: number;
  work_shift_number?: number;
  work_crew_number?: number;
  crew?: number;
  violation_type?: string;
  violation_type_name?: string;
}
interface ViolationsListResponse {
  code: number;
  message: string;
  rigs: RigItem[];
  work_shifts: WorkShift[];
  violation_types: ViolationType[];
  violations_stats: ViolationsStats;
  violations?: ViolationListItem[];
}

// --- Helpers ---
const pad = (n: number) => String(n).padStart(2, '0');
const fmtDateTime = (iso?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return `${pad(d.getDate())}.${pad(d.getMonth()+1)}.${String(d.getFullYear()).slice(-2)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Translate month identifiers to 3-letter Russian abbreviations (supports EN/RU full/short and numeric forms)
const monthToRu = (raw: string): string => {
  const map: Record<string,string> = {
    // English full -> RU short
    'January':'Янв','February':'Фев','March':'Мар','April':'Апр','May':'Май','June':'Июн','July':'Июл','August':'Авг','September':'Сен','October':'Окт','November':'Ноя','December':'Дек',
    // English short -> RU short
    'Jan':'Янв','Feb':'Фев','Mar':'Мар','Apr':'Апр','Jun':'Июн','Jul':'Июл','Aug':'Авг','Sep':'Сен','Oct':'Окт','Nov':'Ноя','Dec':'Дек',
    // Russian full -> RU short
    'Январь':'Янв','Февраль':'Фев','Март':'Мар','Апрель':'Апр','Май':'Май','Июнь':'Июн','Июль':'Июл','Август':'Авг','Сентябрь':'Сен','Октябрь':'Окт','Ноябрь':'Ноя','Декабрь':'Дек'
  };
  const t = raw.trim();
  if (map[t]) return map[t];
  // Numeric month possibly inside patterns like YYYY-MM or MM-YYYY
  const m = t.match(/^(?:\d{4}[-/])?(\d{1,2})(?:[-/]\d{2,4})?$/);
  if (m) {
    const idx = parseInt(m[1],10);
    const abbr = ['Янв','Фев','Мар','Апр','Май','Июн','Июл','Авг','Сен','Окт','Ноя','Дек'];
    if (idx>=1 && idx<=12) return abbr[idx-1];
  }
  return t;
};

const valueToColor = (v: number) => {
  if (v <= 10) return '#32cd32';
  if (v <= 30) return '#ffb347';
  return '#d9534f';
};

// --- Styles ---

const styles: { [key: string]: CSSProperties } = {
  content: { padding: '0 16px 16px 16px', display: "flex", flexDirection: 'column', gap: 12, marginTop: 24 },
    header: { 
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      backgroundColor: '#1F2534',
        padding: 12,
        borderRadius: 4,
        marginBottom: 16
    },
    dropdown: dropdownStyle,
    dateBox: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4
    },
    dateLabel: {
        fontSize: '12px',
        color: '#808080',
        paddingLeft: '2px'
    },
    dateInput: {
      background: '#2C2C2C',
        color: '#fff',
        border: '1px solid #2F3136',
        borderRadius: 4,
        padding: '4px 12px',
        width: '260px',
        height: '28px',
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    },
  button: { 
        background: '#d9534f',
        color: '#fff',
        border: 'none',
        borderRadius: 4,
        padding: '0 16px',
        height: '28px',
        cursor: 'pointer',
        fontSize: '14px',
        marginLeft: 'auto'
    },
  buttonSecondary: {
    background: '#4472C4',
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    padding: '0 16px',
    height: '28px',
    cursor: 'pointer',
    fontSize: '14px',
  },
    calendarButton: {
        background: 'transparent',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '20px',
        height: '20px'
    },
    mainGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 },
    tableContainer: { background: '#1F2534', borderRadius: 16, padding: 20, border: '1px solid #2E3749', boxShadow: '0 2px 16px #0000003D' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { borderBottom: '1px solid #444', padding: '8px', textAlign: 'left', fontSize: 14, color: '#aaa' },
    td: { borderBottom: '1px solid #444', padding: '8px', fontSize: 14 },
    videoPlaceholder: { background: '#000', borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#555', fontSize: 24, minHeight: 400 },
    bottomGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
    summaryContainer: { display: 'flex', gap: 12 },
    summaryCard: { 
      flex: 1, 
      background: '#1F2534', 
        borderRadius: 16, 
        padding: '12px', 
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      height: '200px',
      border: '1px solid #2E3749',
      boxShadow: '0 2px 16px #0000003D'
    },
    // Newly added date picker button styles
    dateField: { display: 'flex', flexDirection: 'column', gap: 4, marginTop: -20 },
    dateButton: {
      background: '#424F67',
      color: '#fff',
      border: '1px solid #2F3136',
      borderRadius: 4,
      padding: '4px 12px',
      width: '260px',
      height: '28px',
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
      cursor: 'pointer'
    },
    dateText: { fontSize: '14px', color: '#fff' },
    dateIcon: { display: 'flex', alignItems: 'center', justifyContent: 'center' }
};

// --- Component ---

const ViolationsPage: FC = () => {
  const { user } = useAuth();
  const token = user?.access_token;
  const [rigs, setRigs] = useState<RigItem[]>([]);
  const [workShifts, setWorkShifts] = useState<WorkShift[]>([]);
  const [violationTypes, setViolationTypes] = useState<ViolationType[]>([]);
  const [selectedRig, setSelectedRig] = useState<string>('');
  const [selectedShift, setSelectedShift] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const now = new Date();
  const dayAgo = new Date(now.getTime() - 24*60*60*1000);
  const [periodFrom, setPeriodFrom] = useState<Date>(dayAgo);
  const [periodTo, setPeriodTo] = useState<Date>(now);
  const [activePicker, setActivePicker] = useState<'from' | 'to' | null>(null);
  const [rows, setRows] = useState<TableViolation[]>([]);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [summary, setSummary] = useState<SummaryData[]>([
    { title: 'Нарушений за неделю', value: 0, color: '#32cd32' },
    { title: 'Нарушений за месяц', value: 0, color: '#ffb347' },
    { title: 'Нарушений за год', value: 0, color: '#d9534f' },
  ]);
  const [monthsChart, setMonthsChart] = useState<ChartData[]>([]);

  // populate dropdowns + stats on mount
  useEffect(() => {
    if (!token) return;
    const fetchInitial = async () => {
      try {
        // Fetch stations list for the station selector via /summary/static (role-safe)
        try {
          const stationsRes = await fetch(apiUrl('/summary/static'), {
            method: 'GET',
            headers: { 'Accept': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
          });
          if (stationsRes.ok) {
            const sdata = await stationsRes.json();
            const list: RigItem[] = Array.isArray(sdata.station_list) ? sdata.station_list.map((s: any) => ({
              id: s.id,
              name: s.name,
            })) : [];
            setRigs(list);
          }
        } catch (e) { console.error(e); }

        const res = await fetch(apiUrl('/violations/list'), {
          method: 'GET',
          headers: { 'Accept': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        });
        if (!res.ok) throw new Error(`violations/list failed: ${res.status}`);
        const data: ViolationsListResponse = await res.json();
        setWorkShifts(data.work_shifts || []);
        setViolationTypes(data.violation_types || []);
        if (data.violations_stats) {
          const s = data.violations_stats;
          setSummary([
            { title: 'Нарушений за неделю', value: s.week || 0, color: valueToColor(s.week || 0) },
            { title: 'Нарушений за месяц', value: s.month || 0, color: valueToColor(s.month || 0) },
            { title: 'Нарушений за год', value: s.year || 0, color: valueToColor(s.year || 0) },
          ]);
          const chart = (s.months || []).map(([name, value]) => ({ name: monthToRu(String(name ?? '')), value: Number(value ?? 0), color: valueToColor(Number(value ?? 0)) }));
          setMonthsChart(chart);
        }
        const list = (data.violations || []).map(v => ({
          id: v.id,
          date: fmtDateTime(v.timestamp || v.date),
          station: v.station_name ?? v.station_number ?? v.station ?? '-',
          brigade: v.work_shift_number ?? v.work_crew_number ?? v.crew ?? '-',
          type: v.violation_type_name ?? v.violation_type ?? '-',
        }));
        setRows(list);
      } catch (e) {
        console.error(e);
      }
    };
    fetchInitial();
  }, [token]);

  const fetchList = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedRig) params.set('station_id', selectedRig);
      if (selectedShift) params.set('work_shift_id', selectedShift);
      if (selectedType) params.set('violation_type_id', selectedType);
      if (periodFrom) params.set('period_from', periodFrom.toISOString());
      if (periodTo) params.set('period_to', periodTo.toISOString());
      const url = `${apiUrl('/violations/list')}${params.toString() ? `?${params.toString()}` : ''}`;
      const res = await fetch(url, {
        method: 'GET',
        headers: { 'Accept': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      });
      if (!res.ok) throw new Error(`violations/list failed: ${res.status}`);
      const data: ViolationsListResponse = await res.json();
      if (data.violations_stats) {
        const s = data.violations_stats;
        setSummary([
          { title: 'Нарушений за неделю', value: s.week || 0, color: valueToColor(s.week || 0) },
          { title: 'Нарушений за месяц', value: s.month || 0, color: valueToColor(s.month || 0) },
          { title: 'Нарушений за год', value: s.year || 0, color: valueToColor(s.year || 0) },
        ]);
        const chart = (s.months || []).map(([name, value]) => ({ name: monthToRu(String(name ?? '')), value: Number(value ?? 0), color: valueToColor(Number(value ?? 0)) }));
        setMonthsChart(chart);
      }
      const list = (data.violations || []).map(v => ({
        id: v.id,
        date: fmtDateTime(v.timestamp || v.date),
        station: v.station_name ?? v.station_number ?? v.station ?? '-',
        brigade: v.work_crew_number ?? v.crew ?? '-',
        type: v.violation_type_name ?? v.violation_type ?? '-',
      }));
      setRows(list);
      setSelectedRowId(null);
      if (imageUrl) { URL.revokeObjectURL(imageUrl); setImageUrl(null); }
    } catch (e) { console.error(e); }
  };

  const onRowClick = async (id: string) => {
    setSelectedRowId(id);
    try {
      const url = `${apiUrl('/violations/image')}?violation_id=${encodeURIComponent(id)}`;
      const res = await fetch(url, {
        method: 'GET',
        headers: { 'Accept': 'image/png', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      });
      if (!res.ok) throw new Error(`violations/image failed: ${res.status}`);
      const blob = await res.blob();
      if (imageUrl) URL.revokeObjectURL(imageUrl);
      const obj = URL.createObjectURL(blob);
      setImageUrl(obj);
    } catch (e) { console.error(e); }
  };

  const summaryData = summary;
  const chartData = monthsChart;

  return (
    <div style={{...styles.content, height: '100%'}}>
      <div style={styles.header}>
        <select style={dropdownStyle} value={selectedRig} onChange={e=>setSelectedRig(e.target.value)}>
          <option value="">Все станки</option>
          {rigs.map(r=> <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        <select style={dropdownStyle} value={selectedShift} onChange={e=>setSelectedShift(e.target.value)}>
          <option value="">Все смены</option>
          {workShifts.map(w=> <option key={w.id} value={w.id}>{w.number}</option>)}
        </select>
        <select style={dropdownStyle} value={selectedType} onChange={e=>setSelectedType(e.target.value)}>
          <option value="">Тип нарушения</option>
          {violationTypes.map(v=> <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <span style={{ color:'#bbb', fontSize:14, marginLeft: 16 }}>с</span>
        <button style={{...styles.dateButton, background: activePicker==='from' ? '#2E3749' : '#424F67' }} onClick={()=>setActivePicker('from')}>
          <span style={styles.dateText}>{`${pad(periodFrom.getDate())}.${pad(periodFrom.getMonth()+1)}.${String(periodFrom.getFullYear()).slice(-2)} ${pad(periodFrom.getHours())}:${pad(periodFrom.getMinutes())}`}</span>
          <span style={styles.dateIcon}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="2" y="3" width="12" height="11" rx="2" stroke="#888" strokeWidth="1"/>
              <path d="M5 1V4" stroke="#888" strokeWidth="1" strokeLinecap="round"/>
              <path d="M11 1V4" stroke="#888" strokeWidth="1" strokeLinecap="round"/>
              <path d="M3 6H13" stroke="#888" strokeWidth="1" strokeLinecap="round"/>
            </svg>
          </span>
        </button>
        <span style={{ color:'#bbb', fontSize:14 }}>по</span>
        <button style={{...styles.dateButton, background: activePicker==='to' ? '#2E3749' : '#424F67' }} onClick={()=>setActivePicker('to')}>
          <span style={styles.dateText}>{`${pad(periodTo.getDate())}.${pad(periodTo.getMonth()+1)}.${String(periodTo.getFullYear()).slice(-2)} ${pad(periodTo.getHours())}:${pad(periodTo.getMinutes())}`}</span>
          <span style={styles.dateIcon}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="2" y="3" width="12" height="11" rx="2" stroke="#888" strokeWidth="1"/>
              <path d="M5 1V4" stroke="#888" strokeWidth="1" strokeLinecap="round"/>
              <path d="M11 1V4" stroke="#888" strokeWidth="1" strokeLinecap="round"/>
              <path d="M3 6H13" stroke="#888" strokeWidth="1" strokeLinecap="round"/>
            </svg>
          </span>
        </button>
        {/* <button style={styles.button}>Показать нарушения</button> */}
        <button style={styles.buttonSecondary} onClick={fetchList}>Отобразить в таблице</button>
      </div>

      <div style={styles.mainGrid}>
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Дата/время</th>
                <th style={styles.th}>Станок</th>
                <th style={styles.th}>Бригада</th>
                <th style={styles.th}>Тип нарушения</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} onClick={()=>onRowClick(row.id)} style={{ background: row.id === selectedRowId ? '#2E3749' : 'transparent', cursor: 'pointer' }}>
                  <td style={styles.td}>{row.date}</td>
                  <td style={styles.td}>{row.station}</td>
                  <td style={styles.td}>{row.brigade}</td>
                  <td style={styles.td}>{row.type}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={styles.videoPlaceholder}>
          {imageUrl ? (
            <img src={imageUrl} alt="violation" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          ) : (
            'Нет изображения'
          )}
        </div>
      </div>

      <div style={styles.bottomGrid}>
        <div style={{ background: '#1F2534', borderRadius: 16, padding: 20 }}>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="name" tick={{ fill: '#aaa' }} />
              <Bar dataKey="value" barSize={20} radius={[0, 8, 8, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div style={styles.summaryContainer}>
            {summaryData.map(item => (
                <div key={item.title} style={styles.summaryCard}>
                    <div style={{ fontSize: 64, fontWeight: 'bold', color: item.color, marginBottom: 8, lineHeight: 1 }}>{item.value}</div>
                    <div style={{ fontSize: 14, color: '#aaa' }}>{item.title}</div>
                </div>
            ))}
        </div>
      </div>
      {activePicker && (
        <DateTimePickerModal
          isOpen={true}
          initialDate={activePicker === 'from' ? periodFrom : periodTo}
          onClose={()=>setActivePicker(null)}
          onSelect={(d)=>{
            if (activePicker === 'from') {
              setPeriodFrom(d);
              if (d > periodTo) setPeriodTo(d);
            } else {
              if (d < periodFrom) setPeriodFrom(d);
              setPeriodTo(d);
            }
            setActivePicker(null);
          }}
        />
      )}
    </div>
  );
}

export default ViolationsPage;
